/**
 * Analytics service for tracking user events and interactions
 * Integrates with analytics providers based on environment configuration
 */

import { metaPixelService } from './metaPixelService';
import { capture as posthogCapture } from '@/services/posthogService';
import type { EventName } from '@/posthog.config';

// Track an event with optional properties
export const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
  try {
    // Check for window to avoid SSR issues
    if (typeof window !== 'undefined') {
      // Log to console in development
      if (import.meta.env.DEV) {
        console.log('[Analytics]', eventName, properties);
      }

      // Send to Google Analytics if available
      if (window.gtag) {
        window.gtag('event', eventName, properties);
      }

      // Send to Clarity if available
      if (window.clarity) {
        window.clarity('track', eventName, properties);
      }

      // Send to Intercom if available
      if (window.Intercom) {
        window.Intercom('trackEvent', eventName, properties);
      }

      // Send to Meta Pixel if available
      if (metaPixelService.isAvailable()) {
        metaPixelService.trackEvent(eventName, properties);
      }

      // Send to PostHog (typed events when possible)
      try {
        posthogCapture(eventName as EventName, properties);
      } catch (posthogError) {
        if (import.meta.env.DEV) {
          console.warn('[PostHog capture error]', posthogError);
        }
      }
    }
  } catch (error) {
    console.error('[Analytics Error]', error);
  }
};

// Track a page view
export const trackPageView = (pageName: string, pageProperties?: Record<string, any>): void => {
  trackEvent('page_view', {
    page_name: pageName,
    ...pageProperties
  });
};

// Track user identification
export const identifyUser = (userId: string, userProperties?: Record<string, any>): void => {
  try {
    if (typeof window !== 'undefined') {
      // Set user ID in Google Analytics if available
      if (window.gtag) {
        window.gtag('set', 'user_id', userId);
        if (userProperties) {
          window.gtag('set', 'user_properties', userProperties);
        }
      }

      // Identify in Clarity if available
      if (window.clarity) {
        window.clarity('identify', userId, userProperties);
      }

      // Identify in Intercom if available
      if (window.Intercom) {
        window.Intercom('update', {
          user_id: userId,
          ...userProperties
        });
      }

      // Update user data in Meta Pixel if available
      if (metaPixelService.isAvailable()) {
        metaPixelService.updateUserData({
          user_id: userId,
          ...userProperties
        });
      }
    }
  } catch (error) {
    console.error('[Analytics Identify Error]', error);
  }
};

// Meta Pixel specific tracking functions
export const trackMetaPixelEvent = (eventName: string, parameters?: Record<string, any>): void => {
  metaPixelService.trackEvent(eventName, parameters);
};

export const trackMetaPixelPurchase = (value: number, currency: string = 'USD', contentIds?: string[]): void => {
  metaPixelService.trackPurchase(value, currency, contentIds);
};

export const trackMetaPixelDonation = (value: number, currency: string = 'USD', campaignId?: string): void => {
  metaPixelService.trackDonation(value, currency, campaignId);
};

export const trackMetaPixelInitiateCheckout = (value: number, currency: string = 'USD', contentIds?: string[]): void => {
  metaPixelService.trackInitiateCheckout(value, currency, contentIds);
};

export const trackMetaPixelAddToCart = (value: number, currency: string = 'USD', contentIds?: string[]): void => {
  metaPixelService.trackAddToCart(value, currency, contentIds);
};

export const trackMetaPixelLead = (): void => {
  metaPixelService.trackLead();
};

export const trackMetaPixelContact = (): void => {
  metaPixelService.trackContact();
};

export const trackMetaPixelCompleteRegistration = (): void => {
  metaPixelService.trackCompleteRegistration();
};

// Initialize Meta Pixel
export const initializeMetaPixel = (pixelId: string, enabled: boolean = true): void => {
  metaPixelService.initialize(pixelId, enabled);
};

// Check if Meta Pixel is available
export const isMetaPixelAvailable = (): boolean => {
  return metaPixelService.isAvailable();
};

// Get Meta Pixel configuration
export const getMetaPixelConfig = () => {
  return metaPixelService.getConfig();
};

// Add TypeScript declarations for analytics providers
declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: any) => void;
    clarity?: (command: string, ...args: any[]) => void;
    Intercom?: IntercomCommand;
  }
}
